export type NumLocale = 'de' | 'en' | 'tr';

const intlLocale: Record<NumLocale, string> = { de: 'de-DE', en: 'en-GB', tr: 'tr-TR' };

/** 450 -> "7,50" (de) / "7.50" (en) */
export function formatHours(minutes: number, locale: NumLocale = 'de', decimals = 2): string {
  const hours = minutes / 60;
  return new Intl.NumberFormat(intlLocale[locale], {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(hours);
}

export function formatNumber(value: number, locale: NumLocale = 'de', decimals = 0): string {
  return new Intl.NumberFormat(intlLocale[locale], {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/** 123456 -> "1.234,56 €" */
export function formatMoney(cents: number, locale: NumLocale = 'de'): string {
  return new Intl.NumberFormat(intlLocale[locale], { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

export function formatPercent(ratio: number, locale: NumLocale = 'de', decimals = 0): string {
  if (!Number.isFinite(ratio)) return '-';
  return new Intl.NumberFormat(intlLocale[locale], {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(ratio);
}

/** Parses "7,5", "7.5", "7:30", "7h 30m" into minutes. Returns null if invalid. */
export function parseHoursInput(input: string): number | null {
  const s = input.trim().toLowerCase().replace(/\s+/g, '');
  if (!s) return null;
  let m = /^(\d{1,2}):(\d{2})$/.exec(s);
  if (m) {
    const min = Number(m[1]) * 60 + Number(m[2]);
    return Number(m[2]) < 60 ? min : null;
  }
  m = /^(\d{1,2})h(?:(\d{1,2})m?)?$/.exec(s);
  if (m) return Number(m[1]) * 60 + Number(m[2] ?? 0);
  m = /^(\d{1,3})m(?:in)?$/.exec(s);
  if (m) return Number(m[1]);
  m = /^(\d{1,2})(?:[.,](\d{1,2}))?$/.exec(s);
  if (m) {
    const frac = m[2] ? Number('0.' + m[2]) : 0;
    return Math.round((Number(m[1]) + frac) * 60);
  }
  return null;
}

export function roundToStep(minutes: number, step: number): number {
  if (step <= 1) return Math.round(minutes);
  return Math.round(minutes / step) * step;
}

/**
 * Forma project names usually start with the project number: "2024-017 Klinikum Nordtal" gives
 * number "2024-017" and name "Klinikum Nordtal". Without a number prefix the whole name is returned.
 */
export function splitFormaName(full: string): { number: string | null; name: string } {
  const m = /^\s*([A-Za-z]{0,4}[0-9][A-Za-z0-9._/-]{2,19})[\s_:-]+(.+?)\s*$/.exec(full);
  if (m && /[0-9]/.test(m[1])) return { number: m[1], name: m[2] };
  return { number: null, name: full.trim() };
}

/**
 * Clash issues of the model coordination (Forma Model Coordination or the Navisworks add-in) land in the locked
 * type "Clash" of the category "Coordination"; companies sometimes name their own types "Kollision". Types with
 * such a name (or below a category with such a name) are excluded from the import until someone includes them.
 */
const CLASH_NAME = /^(clash(es)?|clash detection|kollision(en)?|kollisionspr(ü|ue)fung|(ç|c)ak(ı|i)(ş|s)ma(lar)?|(ç|c)ak(ı|i)(ş|s)ma kontrol(ü|u))$/i;

export function isClashName(title: string | null | undefined): boolean {
  return !!title && CLASH_NAME.test(title.trim());
}
